import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

export default function PinScreen() {
  const { terminalToken, loginPin } = useAuth();
  const [pin, setPin]         = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [upcomingReservations, setUpcomingReservations] = useState([]);

  useEffect(() => {
    fetch('/api/receipt/pin-logo/preview')
      .then(res => { if (!res.ok) throw new Error(); return res.blob(); })
      .then(blob => {
        const reader = new FileReader();
        reader.onload = () => setLogoUrl(reader.result);
        reader.readAsDataURL(blob);
      })
      .catch(() => {});
  }, []);

  // Zbliżające się rezerwacje (najbliższe 30 minut) - widoczne już na ekranie PIN,
  // zanim jeszcze konkretny pracownik się zaloguje, żeby obsługa mogła się przygotować
  useEffect(() => {
    if (!terminalToken) return;
    function loadReservations() {
      fetch('/api/reservations?upcoming=30', { headers: { Authorization: `Bearer ${terminalToken}` } })
        .then(r => r.ok ? r.json() : [])
        .then(setUpcomingReservations)
        .catch(() => {});
    }
    loadReservations();
    const interval = setInterval(loadReservations, 30000);
    return () => clearInterval(interval);
  }, [terminalToken]);

  async function handlePin(digit) {
    const newPin = pin + digit;
    setPin(newPin);
    setError('');
    if (newPin.length === 4) {
      setLoading(true);
      try {
        const res = await fetch('/api/auth/pin', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${terminalToken}`,
          },
          body: JSON.stringify({ pin: newPin }),
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data.error || 'Nieprawidłowy PIN');
          setPin('');
        } else {
          loginPin(data);
          setPin('');
        }
      } catch {
        setError('Błąd połączenia');
        setPin('');
      } finally {
        setLoading(false);
      }
    }
  }

  function handleDelete() {
    setPin(p => p.slice(0, -1));
    setError('');
  }

  const KEYS = [['1','2','3'],['4','5','6'],['7','8','9'],['','0','⌫']];

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)', gap: 32, position: 'relative',
      paddingTop: upcomingReservations.length > 0 ? 60 : 0,
    }}>
      <div style={{ textAlign: 'center' }}>
        
        <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 4 }}>Wpisz swój PIN</div>
      </div>

      {upcomingReservations.length > 0 && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          flexWrap: 'wrap', background: '#E6F1FB', borderBottom: '0.5px solid #B8D8F0',
          padding: '10px 24px',
        }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#185FA5' }}>
            📅 {upcomingReservations.length === 1 ? 'Zbliża się rezerwacja' : `Zbliża się ${upcomingReservations.length} rezerwacje`}
          </span>
          {upcomingReservations.slice(0, 3).map(r => {
            const minutesTo = Math.round((new Date(r.reservation_time) - Date.now()) / 60000);
            const timeLabel = new Date(r.reservation_time).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
            const tableName = r.table_name || `Stolik ${r.table_number}`;
            return (
              <span key={r.id} style={{ fontSize: 12, color: '#185FA5' }}>
                · {timeLabel} · {tableName} · {r.customer_name} ({r.party_size} os.) — {minutesTo <= 0 ? 'teraz' : `za ${minutesTo} min`}
              </span>
            );
          })}
        </div>
      )}

      <div style={{ display: 'flex', gap: 16 }}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{
            width: 16, height: 16, borderRadius: '50%',
            background: i < pin.length ? 'var(--text-primary)' : 'var(--border)',
            transition: 'background .15s',
          }} />
        ))}
      </div>

      {error && (
        <div style={{ color: '#A32D2D', fontSize: 14, fontWeight: 500 }}>{error}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 72px)', gap: 12 }}>
        {KEYS.flat().map((key, i) => (
          <button key={i}
            onClick={() => key === '⌫' ? handleDelete() : key ? handlePin(key) : null}
            disabled={loading || !key}
            style={{
              width: 72, height: 72, borderRadius: 12,
              border: '0.5px solid var(--border)',
              background: key ? 'var(--surface)' : 'transparent',
              fontSize: key === '⌫' ? 20 : 24,
              fontWeight: 500,
              color: key ? 'var(--text-primary)' : 'transparent',
              cursor: key ? 'pointer' : 'default',
              opacity: loading ? 0.5 : 1,
            }}
          >
            {key}
          </button>
        ))}
      </div>
      <div style={{ position: 'absolute', left: 24, bottom: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
        {logoUrl && (
          <img src={logoUrl} alt="Logo lokalu" style={{ maxHeight: 132, maxWidth: 480, objectFit: 'contain' }} />
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="64" height="64" viewBox="0 0 64 64">
            <path d="M16 8 L48 8 L48 42 L44 50 L40 42 L36 50 L32 42 L28 50 L24 42 L20 50 L16 42 L16 8 Z" fill="#2563EB" />
            <line x1="22" y1="18" x2="42" y2="18" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" />
            <line x1="22" y1="26" x2="42" y2="26" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" />
            <line x1="22" y1="34" x2="36" y2="34" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" />
          </svg>
          <span style={{ fontSize: 48, fontWeight: 600, color: 'var(--text-muted)' }}>
            POS<span style={{ fontWeight: 400, color: '#2563EB' }}>it</span>
          </span>
        </div>
      </div>
    </div>
  );
}
